// ─── Farm Adventure content pack ───
// Extracted verbatim from DemoApp.jsx (2026-08-07) as part of adding the
// Playground adventure — the game engine stays in DemoApp; everything that is
// specific to ONE adventure (cards, deck algorithm, background, STT vocab)
// lives in a pack like this one. See playground.js for the second instance.
//
// Per Natalya & Polina (Apr 2026): single cards were retired in favor of
// multi-animal cards because (a) they teach proper plurals, (b) every multi
// has a clear, single dominant color (the original "1 black-and-white
// chicken" was ambiguous), and (c) they give the kid more practice per card.
// Turkey stays as the only single because turkey coloring is too varied for
// kids to identify reliably — it's asked only the name question.
//
// plural:    used in questions/responses
// type:      'animal' or 'bird' — used for question variety
// nameOnly:  if true, only the "what is this?" question is asked

const QUESTIONS_PER_GAME = 10;

const pool = [
  // Multi-animal cards
  { id: 'cow-multi', image: '/images/unit1/Two Black Cows.png', word: 'cow', plural: 'cows', color: 'black', count: 2, type: 'animal' },
  { id: 'pig-multi', image: '/images/unit1/Two Pink Pigs.png', word: 'pig', plural: 'pigs', color: 'pink', count: 2, type: 'animal' },
  { id: 'chicken-multi', image: '/images/unit1/Three Brown Chickens.png', word: 'chicken', plural: 'chickens', color: 'brown', count: 3, type: 'bird' },
  { id: 'horse-multi', image: '/images/unit1/Three Brown Horses.png', word: 'horse', plural: 'horses', color: 'brown', count: 3, type: 'animal' },
  { id: 'duck-multi', image: '/images/unit1/Four Brown Ducks.png', word: 'duck', plural: 'ducks', color: 'brown', count: 4, type: 'bird', skipColor: true },
  { id: 'sheep-multi', image: '/images/unit1/Four White Sheep.png', word: 'sheep', plural: 'sheep', color: 'white', count: 4, type: 'animal' },
  { id: 'goose', image: '/images/unit1/Geese.png', word: 'goose', plural: 'geese', color: 'white', count: 2, type: 'bird' },
  { id: 'rabbit', image: '/images/unit1/Rabbits.png', word: 'rabbit', plural: 'rabbits', color: 'brown', count: 2, type: 'animal' },
  // Special single card — name only, no color or count
  { id: 'turkey', image: '/images/unit1/Turkey.png', word: 'turkey', plural: 'turkeys', color: 'brown', count: 1, type: 'bird', nameOnly: true },
];

// Build a deck that always totals QUESTIONS_PER_GAME (10) questions.
//
// Each multi card normally asks 3 questions (name + color + count). Some
// cards skip a tier — e.g. duck-multi has skipColor:true so it asks only
// 2 questions (name + count) because duck coloring is hard to identify.
//
// Algorithm:
//   1. Pick 3 multi cards with unique words for the full-tier slots.
//   2. Count the questions used (8 if a duck snuck in, 9 otherwise).
//   3. Top up with name-only cards (1 question each) until we hit 10.
//      Name-only candidates: turkey + any multi whose word isn't taken.
//
// So a typical round is either:
//   • 3 normal multis (9q) + 1 name-only (1q)        = 10q, 4 cards
//   • 2 normal multis + duck-multi (8q) + 2 name-only = 10q, 5 cards
function buildDeck() {
  const turkey = pool.find((a) => a.id === 'turkey');
  const multiPool = pool.filter((a) => a.count > 1);

  const tiersFor = (card) => card.skipColor ? ['name', 'count'] : ['name', 'color', 'count'];

  // Pick 3 multi cards with unique words for the full-tier slots
  const shuffledMulti = [...multiPool].sort(() => Math.random() - 0.5);
  const fullMultis = [];
  const usedWords = new Set();
  for (const m of shuffledMulti) {
    if (fullMultis.length >= 3) break;
    if (!usedWords.has(m.word)) {
      fullMultis.push({ ...m, tiers: tiersFor(m) });
      usedWords.add(m.word);
    }
  }

  // How many questions still need to be filled with name-only cards?
  const questionsUsed = fullMultis.reduce((sum, c) => sum + c.tiers.length, 0);
  const slotsRemaining = QUESTIONS_PER_GAME - questionsUsed;

  // Name-only candidates: turkey + any multi whose word isn't already used
  const remainingMultis = multiPool.filter((m) => !usedWords.has(m.word));
  const nameOnlyCandidates = [turkey, ...remainingMultis].sort(() => Math.random() - 0.5);
  const nameOnlyCards = nameOnlyCandidates
    .slice(0, slotsRemaining)
    .map((c) => ({ ...c, tiers: ['name'] }));

  return [...fullMultis, ...nameOnlyCards].sort(() => Math.random() - 0.5);
}

const plurals = pool.map((a) => a.plural).join(', ');

export default {
  id: 'farm',
  title: 'Farm Adventure',
  pool,
  buildDeck,
  // Farm ends with the open-ended "What is your favorite farm animal?" flow.
  hasFavorite: true,
  // Open-ended finale config (generalized 2026-08-11 — the engine reads
  // ADVENTURE.favorite; these are Farm's original values, moved verbatim
  // from DemoApp.jsx). `options` omitted → matcher uses the card pool, whose
  // 9 animals all have pre-baked favorite-<word>.mp3 celebration audio.
  favorite: {
    question: 'What is your favorite farm animal?',
    askAudio: '/audio/favorite-ask.mp3',
    image: '/images/unit1/All-Farm-Animals.jpg',
    judgeKind: 'farm animal',
    // Curated farm animals beyond the pool — matched instantly locally,
    // celebrated with a live named Puck line (no pre-baked file).
    // NOTE: no 'ox' — fuzzyMatch's substring rule would match it inside
    // "fox"/"box". Rare answer; the Gemini judge handles it correctly.
    extras: [
      { word: 'quail', plural: 'quails' },
      { word: 'goat', plural: 'goats' },
      { word: 'donkey', plural: 'donkeys' },
      { word: 'mule', plural: 'mules' },
      { word: 'bull', plural: 'bulls' },
      { word: 'llama', plural: 'llamas' },
      { word: 'alpaca', plural: 'alpacas' },
      { word: 'pony', plural: 'ponies' },
    ],
    // Alt/baby names → pool word (celebrates as the mapped animal).
    synonyms: {
      bunny: 'rabbit', bunnies: 'rabbit',
      hen: 'chicken', rooster: 'chicken', chick: 'chicken',
      duckling: 'duck', gosling: 'goose',
      lamb: 'sheep', calf: 'cow', foal: 'horse', piglet: 'pig',
    },
    missKnown: { line: "That's not a farm animal.", audio: '/audio/favorite-not-farm.mp3' },
    missUnknown: { line: "That's not an animal.", audio: '/audio/favorite-not-animal.mp3' },
    hint: { line: "Let's pick together. How about a cow or a horse?", audio: '/audio/favorite-hint.mp3' },
  },
  background: '/images/farm-background.webp',
  // Listener hint for the name tier — enumerating the small fixed vocabulary
  // anchors isolated words that mangle alone ("geese" → "gies").
  sttNameContext: `a farm animal name, singular or plural (${plurals})`,
  sttFavoriteContext: `a farm animal name (${plurals})`,
  // Judge context (2026-08-11): sent with /api/evaluate so the judge prompt
  // describes THIS adventure instead of assuming Farm everywhere.
  judgeTopic: 'a farm picture card — the child names the animal, its color, or how many there are',
  judgeHints: 'Spanish: vacas=cows, pollos=chickens, caballos=horses, patos=ducks, ovejas=sheep, '
    + 'conejos=rabbits, gansos=geese, cerdos=pigs, pavo=turkey; marrón/café=brown, negro=black, '
    + 'blanco=white, rosado=pink; dos=two, tres=three, cuatro=four. '
    + 'Russian: корова=cow, свинья=pig, курица=chicken, лошадь=horse, утка=duck, овца=sheep, '
    + 'гусь=goose, кролик=rabbit, индюк=turkey',
};
